import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  MessageSquare, Loader2, Power, Pause, Play, User, X, Check, Zap, Ticket,
  Plus, Edit3, Trash2, Globe, Lock, Settings2, ChevronDown,
} from 'lucide-react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import DoubtChatView from '../components/DoubtChatView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComplaintChatAgent() {
  const [status, setStatus] = useState(null);
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null); // {chat, messages}
  const [loading, setLoading] = useState(true);
  const [showPresets, setShowPresets] = useState(false);
  const [presets, setPresets] = useState([]);
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [closingChatId, setClosingChatId] = useState(null);
  const [registeringChatId, setRegisteringChatId] = useState(null);
  const [solutionRemarks, setSolutionRemarks] = useState('');
  const user = authService.getCurrentUser();

  const fetchAll = async () => {
    try {
      const [s, c] = await Promise.all([
        api.getComplaintAgentStatus(),
        api.getComplaintAgentChats(false),
      ]);
      setStatus(s.data);
      setChats(c.data || []);
    } catch (e) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const fetchPresets = async () => {
    try {
      const r = await api.getComplaintPresets();
      setPresets(r.data || []);
    } catch (e) { /* silent */ }
  };

  // Heartbeat every 15s to stay "online"
  useEffect(() => {
    const beat = async () => {
      try { await api.complaintAgentHeartbeat(); } catch (e) { /* silent */ }
    };
    beat();
    fetchAll();
    fetchPresets();
    const hbTimer = setInterval(beat, 15000);
    const listTimer = setInterval(fetchAll, 10000);
    return () => { clearInterval(hbTimer); clearInterval(listTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Agent lobby WS — get immediate ping when a chat is assigned to me
  useEffect(() => {
    const token = authService.getToken();
    if (!token) return;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    let ws;
    let retry = 0;
    let alive = true;
    const connect = () => {
      try {
        ws = new WebSocket(`${base}/api/complaint-chat/ws/agent-lobby?token=${encodeURIComponent(token)}`);
        ws.onmessage = (evt) => {
          try {
            const d = JSON.parse(evt.data);
            if (d.type === 'assigned' || d.type === 'queue_update' || d.type === 'chat_update') {
              fetchAll();
            }
          } catch (e) { /* ignore */ }
        };
        ws.onopen = () => { retry = 0; };
        ws.onclose = () => {
          if (!alive) return;
          retry++;
          setTimeout(connect, Math.min(1000 * (2 ** retry), 15000));
        };
      } catch (e) { /* noop */ }
    };
    connect();
    return () => { alive = false; try { ws?.close(); } catch (e) { /* noop */ } };
  }, []);

  const openChat = async (chat) => {
    try {
      const r = await api.getComplaintChatMessages(chat.id);
      setSelected({ chat, messages: r.data.messages || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot open chat');
    }
  };

  const togglePause = async () => {
    try {
      const r = await api.setComplaintAgentStatus({ is_paused: !status?.is_paused });
      setStatus((s) => ({ ...s, ...r.data }));
      toast.success(r.data.is_paused ? 'Paused — you will not receive new chats' : 'Resumed — accepting new chats');
    } catch (e) { toast.error('Could not update status'); }
  };

  const changeCapacity = async (n) => {
    try {
      const r = await api.setComplaintAgentStatus({ max_concurrent: n });
      setStatus((s) => ({ ...s, ...r.data }));
      toast.success(`Capacity set to ${r.data.max_concurrent}`);
    } catch (e) { toast.error('Could not update capacity'); }
  };

  const closeChat = async () => {
    if (!closingChatId) return;
    try {
      const r = await api.closeComplaintChat(closingChatId, solutionRemarks);
      toast.success(`Chat closed — ticket ${r.data.ticket_number} created`);
      setClosingChatId(null);
      setSolutionRemarks('');
      // Clear selection if we just closed the visible chat
      if (selected?.chat?.id === closingChatId) setSelected(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not close');
    }
  };

  const registerTicket = async () => {
    if (!registeringChatId) return;
    try {
      const r = await api.registerComplaintTicket(registeringChatId, solutionRemarks);
      toast.success(`Complaint registered — ticket ${r.data.ticket_number} (24 h SLA)`);
      setRegisteringChatId(null);
      setSolutionRemarks('');
      if (selected?.chat?.id === registeringChatId) setSelected(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not register');
    }
  };

  const insertPreset = async (text) => {
    if (!selected) return;
    try {
      const r = await api.sendComplaintChatText(selected.chat.id, text);
      setSelected((s) => (s ? { ...s, messages: [...s.messages, r.data] } : s));
      setShowPresetDropdown(false);
    } catch (e) {
      toast.error('Failed to send preset');
    }
  };

  const wsUrl = (chatId) => {
    const token = authService.getToken();
    if (!token || !chatId) return null;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/complaint-chat/ws/${chatId}?token=${encodeURIComponent(token)}&role=agent`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  }

  const activeCount = status?.active_count || 0;
  const cap = status?.max_concurrent || 4;
  const capacityFull = activeCount >= cap;
  const isPaused = !!status?.is_paused;

  return (
    <div data-testid="complaint-chat-agent" className="max-w-[1600px] mx-auto">
      <div className="mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Live Complaint Chat</h1>
          <p className="text-sm text-slate-600 mt-1">
            Handling <span className="font-semibold text-slate-900">{activeCount}/{cap}</span> chats
            {' · '}Queue: <span className="font-semibold">{status?.waiting_count || 0}</span> waiting
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Capacity picker */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="complaint-capacity">
            <span className="text-xs text-slate-500 px-2">Capacity</span>
            {[2, 3, 4, 5, 6, 8].map((n) => (
              <button key={n} onClick={() => changeCapacity(n)}
                      data-testid={`capacity-${n}`}
                      className={`w-7 h-7 text-xs rounded ${cap === n ? 'bg-cyan-600 text-white font-bold' : 'text-slate-600 hover:bg-slate-100'}`}>
                {n}
              </button>
            ))}
          </div>
          <button onClick={togglePause} data-testid="complaint-pause-toggle"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                    isPaused ? 'bg-amber-500 hover:bg-amber-400 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  }`}>
            {isPaused ? <><Play size={16} /> Resume</> : <><Pause size={16} /> Pause</>}
          </button>
          <button onClick={() => { setShowPresets(true); fetchPresets(); }}
                  data-testid="complaint-presets-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
            <Settings2 size={16} /> Presets
          </button>
        </div>
      </div>

      {/* Alert bar for paused / capacity full */}
      {isPaused && (
        <div className="mb-3 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2">
          <Pause size={14} /> You are paused — new chats won't be assigned to you.
        </div>
      )}
      {capacityFull && !isPaused && (
        <div className="mb-3 px-4 py-2 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-800 text-sm flex items-center gap-2">
          <Zap size={14} /> You're at max capacity — resolve a chat to receive new ones.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-3 bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ minHeight: '75vh' }}>
        {/* Chat list */}
        <div className="border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b text-xs uppercase text-slate-500 font-semibold tracking-wider">
            My active chats ({chats.length})
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {chats.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                <MessageSquare size={36} className="mx-auto mb-2 text-slate-300" />
                No active chats. Sit tight — you'll be assigned when a complaint comes in.
              </div>
            )}
            {chats.map((c) => (
              <button
                key={c.id}
                onClick={() => openChat(c)}
                data-testid={`complaint-chat-item-${c.id}`}
                className={`w-full text-left p-3 hover:bg-slate-50 transition ${selected?.chat?.id === c.id ? 'bg-cyan-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.student_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.problem_type} · {c.student_phone}</div>
                    <div className="text-xs text-slate-500 truncate mt-1">{c.last_message_preview || 'No messages yet'}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 whitespace-nowrap self-start">
                    {c.last_message_at ? new Date(c.last_message_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat surface */}
        <div className="flex flex-col min-h-[75vh]">
          {!selected && (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
              <User size={64} className="opacity-30" />
              <p className="mt-3 text-sm">Select a chat to reply</p>
            </div>
          )}
          {selected && (
            <>
              <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{selected.chat.student_name}</div>
                  <div className="text-xs text-slate-500 truncate">{selected.chat.problem_type} · {selected.chat.student_phone}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <button
                      onClick={() => setShowPresetDropdown((v) => !v)}
                      data-testid="complaint-preset-dropdown"
                      className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 flex items-center gap-1"
                    >
                      <Zap size={14} /> Preset <ChevronDown size={14} />
                    </button>
                    {showPresetDropdown && (
                      <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-30 max-h-80 overflow-y-auto">
                        {presets.length === 0 ? (
                          <div className="p-3 text-xs text-slate-500">No presets yet. Click "Presets" to create.</div>
                        ) : presets.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => insertPreset(p.text)}
                            data-testid={`complaint-preset-send-${p.id}`}
                            className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-0"
                          >
                            <div className="text-xs text-slate-500 flex items-center gap-1 mb-0.5">
                              {p.scope === 'shared' ? <Globe size={11} /> : <Lock size={11} />}
                              {p.created_by_name} · {p.scope}
                            </div>
                            <div className="text-sm text-slate-800 whitespace-pre-wrap line-clamp-3">{p.text}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setRegisteringChatId(selected.chat.id); setSolutionRemarks(''); }}
                    data-testid="complaint-register-btn"
                    className="px-3 py-1.5 rounded-lg text-sm bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1"
                    title="Register as complaint ticket with 24h SLA"
                  >
                    <Ticket size={14} /> Register Complaint
                  </button>
                  <button
                    onClick={() => { setClosingChatId(selected.chat.id); setSolutionRemarks(''); }}
                    data-testid="complaint-close-btn"
                    className="px-3 py-1.5 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                  >
                    <Check size={14} /> Resolve & Close
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <DoubtChatView
                  chatId={selected.chat.id}
                  wsUrl={wsUrl(selected.chat.id)}
                  identity="agent"
                  initialMessages={selected.messages}
                  hideVoice={false}
                  header={null}
                  onSendText={async (content) => (await api.sendComplaintChatText(selected.chat.id, content)).data}
                  onSendMedia={async (file, mediaType) => (await api.sendComplaintChatMedia(selected.chat.id, file, mediaType)).data}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Presets management modal */}
      {showPresets && (
        <PresetsModal
          onClose={() => setShowPresets(false)}
          canShare={['admin', 'manager'].includes(user?.role)}
          userId={user?.id}
        />
      )}

      {/* Close chat confirm modal */}
      {closingChatId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl w-full max-w-md p-5">
            <h3 className="font-semibold text-slate-900 mb-2">Resolve & close chat</h3>
            <p className="text-xs text-slate-500 mb-3">A ticket will be created with the transcript for records.</p>
            <textarea
              value={solutionRemarks}
              onChange={(e) => setSolutionRemarks(e.target.value)}
              placeholder="Resolution summary (optional)"
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="complaint-close-remarks"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setClosingChatId(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              <button onClick={closeChat} data-testid="complaint-close-confirm"
                      className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700">
                Close chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Register complaint (24h SLA) modal */}
      {registeringChatId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl w-full max-w-md p-5">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Ticket size={18} className="text-indigo-600" /> Register as complaint
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              This will create an <span className="font-semibold text-slate-700">open</span> ticket with a
              24-hour SLA and send the ticket ID to the complainant in the chat.
              The chat will be closed on your side.
            </p>
            <textarea
              value={solutionRemarks}
              onChange={(e) => setSolutionRemarks(e.target.value)}
              placeholder="Ticket description / notes for the team (optional)"
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="complaint-register-remarks"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setRegisteringChatId(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              <button onClick={registerTicket} data-testid="complaint-register-confirm"
                      className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 inline-flex items-center gap-1">
                <Ticket size={14} /> Register ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Presets CRUD modal — used by agents to manage their personal presets
 * (and admin/manager to also create shared presets).
 */
function PresetsModal({ onClose, canShare, userId }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [scope, setScope] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.getComplaintPresets();
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const t = text.trim();
    if (!t) { toast.error('Please enter preset text'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.updateComplaintPreset(editingId, { text: t, scope });
        toast.success('Preset updated');
      } else {
        await api.createComplaintPreset({ text: t, scope });
        toast.success('Preset created');
      }
      setText(''); setEditingId(null); setScope('personal');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.id); setText(p.text); setScope(p.scope);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try { await api.deleteComplaintPreset(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" data-testid="complaint-presets-modal">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Message Presets</h3>
          <button onClick={onClose} className="text-slate-500"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-2">{editingId ? 'Edit preset' : 'New preset'}</div>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                      placeholder="e.g. Hi, can you please share your registered mobile number?"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                      data-testid="complaint-preset-text" />
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1"><input type="radio" checked={scope === 'personal'} onChange={() => setScope('personal')} /> <Lock size={12} /> Personal</label>
                {canShare && <label className="flex items-center gap-1"><input type="radio" checked={scope === 'shared'} onChange={() => setScope('shared')} /> <Globe size={12} /> Shared with all</label>}
              </div>
              <div className="flex items-center gap-2">
                {editingId && (
                  <button onClick={() => { setEditingId(null); setText(''); setScope('personal'); }}
                          className="text-xs text-slate-500 hover:text-slate-800">Cancel edit</button>
                )}
                <button onClick={save} disabled={saving}
                        data-testid="complaint-preset-save"
                        className="px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700 disabled:opacity-60 inline-flex items-center gap-1">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                  {editingId ? 'Save' : 'Add preset'}
                </button>
              </div>
            </div>
          </div>

          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No presets yet</div>}
            {items.map((p) => {
              const canEdit = p.created_by === userId || canShare;
              return (
                <div key={p.id} className="p-3 flex items-start justify-between gap-3" data-testid={`complaint-preset-item-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 flex items-center gap-1 mb-0.5">
                      {p.scope === 'shared' ? <Globe size={11} /> : <Lock size={11} />}
                      {p.scope} · by {p.created_by_name}
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{p.text}</div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit"><Edit3 size={14} /></button>
                      <button onClick={() => del(p.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded" title="Delete"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
